import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, InputField } from '@xver/components/Atoms';

interface CouponRedeemFormProps {
    /** Reject to keep the entered code; the caller reports the error. */
    onSubmit: (code: string) => void | Promise<unknown>;
    isSubmitting?: boolean;
    disabled?: boolean;
}

export default function CouponRedeemForm({
    onSubmit,
    isSubmitting,
    disabled,
}: CouponRedeemFormProps) {
    const { t } = useTranslation('coupon');
    const [code, setCode] = useState('');
    const [touched, setTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const validation = useMemo(() => {
        const trimmed = code.trim();
        if (!trimmed) return t('errors.codeRequired');
        if (trimmed.length < 4 || trimmed.length > 32) return t('errors.codeLength');
        return undefined;
    }, [code, t]);

    const visibleError = touched || submitAttempted ? validation : undefined;

    const handleSubmit = async () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (validation) return;
        try {
            await onSubmit(code.trim().toUpperCase());
        } catch {
            return;
        }
        setCode('');
        setTouched(false);
        setSubmitAttempted(false);
    };

    return (
        <div className="w-full p-4 flex flex-col gap-4">
            <InputField
                size="md"
                label={t('form.code')}
                placeholder={t('form.codePlaceholder')}
                value={code}
                onChange={(e) => {
                    setCode(e.target.value);
                    if (!touched) setTouched(true);
                }}
                onBlur={() => setCode((v) => v.trim().toUpperCase())}
                error={visibleError}
                disabled={disabled}
            />
            <div className="flex justify-end">
                <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={disabled}>
                    {t('form.submit')}
                </Button>
            </div>
        </div>
    );
}
